import React from "react"
import { useRouter } from "next/router"

// components
import {
  Row,
  Space,
  Typography,
  Button,
  Divider,
  message,
  Alert,
  Flex,
  Popover,
  Avatar,
} from "antd"
import {
  ClockCircleFilled,
  LoadingOutlined,
  EuroCircleFilled,
  CreditCardFilled,
  InfoCircleOutlined,
  SendOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons"
import OrderItemList from "./OrderItemList"
import GoogleStaticImage from "./GoogleStaticImage"
import Logo from "./Logo"

// configs
import config from "config"

// firebase
import { Functions } from "services/firebase"
import { httpsCallable } from "firebase/functions"

// context
import { useTranslation } from "react-i18next"
import { useOrders } from "store/orders"

// utils
import { formatAsCurrency } from "utils/currency"

const OrderDrawer = ({ order, back, onFinish }) => {
  const router = useRouter()
  const orders = useOrders()
  const { t } = useTranslation()

  const [messageApi, contextHolder] = message.useMessage()

  const [paymentMethod, setPaymentMethod] = React.useState("resto")
  const [route, setRoute] = React.useState()
  const [state, setState] = React.useState("idle")

  React.useEffect(() => {
    const asyncHandlers = async () => {
      try {
        // street: values.street || null,
        // zip: values.zip || null,
        // city: values.city || null,

        setState("load-geocode")

        const { data } = await httpsCallable(
          Functions,
          "getLatLngFromAddress"
        )({ order, resto: config.location })

        setRoute(data)
        setState("idle")
      } catch (e) {
        if (e.code === "functions/unauthenticated") {
          setState("error")
        } else {
          setState("idle")
        }
      }
    }

    if (route || !order) return

    if (order.type !== "delivery") {
      setState("idle")
      return
    }

    asyncHandlers()
  }, [order, route, messageApi])

  const extraDistanceCosts = React.useMemo(() => {
    if (!config.cost_by_distance) return undefined
    if (!route) return null

    const distanceInKm = route.distance_in_meters / 1000

    const list = Object.entries(config.cost_by_distance)
    const item = list.find(([km, price]) => distanceInKm < km)
    return item ? item[1] : list.reverse()[0][1]
  }, [route])

  const deliveryCostsVAT = extraDistanceCosts
    ? (extraDistanceCosts * config.cost_by_distance_vat_rate) /
      (100 + config.cost_by_distance_vat_rate)
    : 0

  const total =
    orders.order.total +
    (extraDistanceCosts || 0) -
    (order.type === "self-pick-up" ? orders.order.pickupDiscounts || 0 : 0)

  const appFee =
    paymentMethod === "card"
      ? total * config.app_fee_percentage > config.app_fee_max
        ? config.app_fee_max
        : total * config.app_fee_percentage
      : 0

  const orderTotal = total + appFee

  if (!order) {
    return (
      <Space>
        <LoadingOutlined />

        <Typography.Text>{t("loading_order")}</Typography.Text>
      </Space>
    )
  }

  return (
    <div>
      {contextHolder}
      <Button
        type="dashed"
        onClick={() => {
          back()
        }}
        icon={<ArrowLeftOutlined />}
      >
        {t("back")}
      </Button>
      <Divider />
      {order.type === "delivery" && state === "load-geocode" && (
        <div>
          <Space>
            <LoadingOutlined />
            <Typography.Text>{t("loading_address")}</Typography.Text>
          </Space>
        </div>
      )}
      {order.type === "delivery" && state === "idle" && !route && (
        <Alert
          message={t("address_not_found")}
          type="warning"
          showIcon
          icon={<ClockCircleFilled />}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => {
                back()
              }}
            >
              {t("check_address")}
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {order.type === "delivery" && state === "error" && (
        <Alert
          message={t("login_expired")}
          type="error"
          showIcon
          icon={<ClockCircleFilled />}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => {
                window.location.reload()
              }}
            >
              {t("reload")}
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Typography.Title level={3}>{t("your_order")}</Typography.Title>

      <Typography.Title level={4}>
        {order.type === "eat-in"
          ? `${t("table")}: ${order.table}`
          : order.type === "delivery"
          ? t("delivery")
          : t("pickup")}
      </Typography.Title>

      <Typography.Paragraph>
        <OrderItemList items={orders.order.list} />
      </Typography.Paragraph>

      <Divider />

      <Flex gap={8}>
        <Typography.Text>{t("subtotal")}:</Typography.Text>
        <Typography.Text>
          {formatAsCurrency(orders.order.total)}
        </Typography.Text>
      </Flex>

      {order.type === "self-pick-up" && orders.order.pickupDiscounts > 0 && (
        <Flex gap={8}>
          <Typography.Text>{t("pickup_discount")}:</Typography.Text>
          <Typography.Text>
            {formatAsCurrency(orders.order.pickupDiscounts)}
          </Typography.Text>
        </Flex>
      )}

      {(order.type === "eat-in" ? orders.order.mainVat : orders.order.vat) +
        deliveryCostsVAT >
        0 && (
        <Flex gap={8}>
          <Typography.Text>{t("incl_vat")}:</Typography.Text>
          <Typography.Text>
            {formatAsCurrency(
              (order.type === "eat-in"
                ? orders.order.mainVat
                : orders.order.vat) + deliveryCostsVAT
            )}
          </Typography.Text>
        </Flex>
      )}

      {order.type === "delivery" && (
        <Flex gap={8}>
          <Typography.Text>
            {t("delivery_fee")}{" "}
            <Popover
              trigger={"click"}
              content={
                <div>
                  {config.cost_by_distance ? (
                    <div>
                      <Typography.Paragraph>
                        {t("delivery_fee_info")}
                      </Typography.Paragraph>

                      {Object.entries(config.cost_by_distance).map((item) => (
                        <Typography.Paragraph
                          style={{ margin: 0 }}
                          key={item[0]}
                        >
                          {`< ${item[0]}km: +${item[1]} EUR`}
                        </Typography.Paragraph>
                      ))}
                    </div>
                  ) : (
                    <Typography.Paragraph>
                      {t("no_delivery_fees")}
                    </Typography.Paragraph>
                  )}
                </div>
              }
            >
              <InfoCircleOutlined />
            </Popover>{" "}
            :{" "}
          </Typography.Text>

          {extraDistanceCosts !== null ? (
            <Typography.Text>
              {formatAsCurrency(extraDistanceCosts || 0)}
            </Typography.Text>
          ) : (
            <Space>
              <LoadingOutlined />
              <Typography.Text type="secondary" italic>
                {t("calculating_delivery_fee")}
              </Typography.Text>
            </Space>
          )}
        </Flex>
      )}

      <Typography.Text>
        {t("service_fee")}{" "}
        <Popover trigger={"click"} content={t("service_fee_info")}>
          <InfoCircleOutlined />
        </Popover>{" "}
        : {formatAsCurrency(paymentMethod === "card" ? appFee : 0)}
      </Typography.Text>

      <Divider />

      {order.type === "delivery" && (
        <>
          <Typography.Title level={4}>{t("delivery_address")}</Typography.Title>

          <Alert
            message={
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                <Typography.Text>
                  <Typography.Text italic>
                    {order?.street} <br />
                    {order?.zip} {order?.city} <br />
                  </Typography.Text>
                  {route && (
                    <Typography.Text type="secondary" italic>
                      {t("delivery_distance")}:{" "}
                      {route && (route.distance_in_meters / 1000).toFixed(2)} km
                    </Typography.Text>
                  )}
                </Typography.Text>
              </Typography.Paragraph>
            }
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Divider />
        </>
      )}

      <Typography.Title level={4}>
        {t("total")}: {formatAsCurrency(orderTotal)}
      </Typography.Title>

      <Divider />

      <>
        <Typography.Title level={4}>{t("payment_method")}</Typography.Title>

        <Space direction="vertical" style={{ width: "100%" }} size="small">
          <Button
            color={paymentMethod === "resto" ? "blue" : "dark"}
            variant={paymentMethod === "resto" ? "filled" : "text"}
            onClick={() => setPaymentMethod("resto")}
          >
            <Flex gap="small" justify="center" align="center">
              <EuroCircleFilled />
              {order.type === "delivery"
                ? t("cash_on_delivery")
                : order.type === "eat-in"
                ? t("cash_at_table")
                : t("cash_on_pickup")}
            </Flex>
          </Button>

          {config?.payments && (
            <Button
              disabled={!config?.payments}
              color={paymentMethod === "card" ? "blue" : "dark"}
              variant={paymentMethod === "card" ? "filled" : "text"}
              onClick={() => setPaymentMethod("card")}
            >
              <Flex gap="small" justify="center" align="center">
                <CreditCardFilled />
                {t("pay_now")}

                <Flex
                  gap="small"
                  justify="center"
                  align="center"
                  style={{ opacity: !config?.payments ? 0.3 : 1 }}
                >
                  <Avatar
                    src="./icons-payment/visa.png"
                    size="small"
                    shape="square"
                  />
                  <Avatar
                    src="./icons-payment/mastercard.png"
                    size="small"
                    shape="square"
                  />
                  <Avatar
                    src="./icons-payment/apple-pay.png"
                    size="small"
                    shape="square"
                  />
                  <Avatar
                    src="./icons-payment/google-pay.png"
                    size="small"
                    shape="square"
                  />
                </Flex>
              </Flex>
            </Button>
          )}
        </Space>

        <Divider />
      </>

      <Row justify="start">
        {paymentMethod === "card" && (
          <Typography.Paragraph type="secondary">
            {t("card_payment_info1")}
            <span
              style={{ transform: "translateY(4px)", display: "inline-block" }}
            >
              <Logo />
            </span>
            {t("card_payment_info2")}
          </Typography.Paragraph>
        )}

        <Button
          size="large"
          type="primary"
          icon={
            paymentMethod === "resto" ? <SendOutlined /> : <CreditCardFilled />
          }
          loading={state === "checkout-order" || state === "send-order"}
          disabled={order?.type === "delivery" && !route}
          onClick={async () => {
            try {
              setState("send-order")

              const orderToSend = {
                version: process.env.version || null,
                ...order,
                route: route || null,
                distance: route ? route.distance_in_meters / 1000 : null,
                extra_distance_costs: extraDistanceCosts || 0,
                pick_up_discounts: orders.order.pickupDiscounts || 0,

                table: order.table || null,

                payment_method: paymentMethod,
                payment_status: "pending",
              }

              if (paymentMethod === "resto") {
                const { data } = await httpsCallable(
                  Functions,
                  "createOrder"
                )(orderToSend)

                console.log("Order created:", data)

                setRoute(null)
                setState("idle")
                onFinish(data)
              } else if (paymentMethod === "card") {
                const { data } = await httpsCallable(
                  Functions,
                  "createPayment"
                )(orderToSend)

                console.log("Payment created:", data)

                setRoute(null)
                setState("idle")

                // route to payment link
                router.push(data)
              } else {
                throw new Error(t("invalid_payment_method"))
              }
            } catch (e) {
              messageApi.error(e.message, 6000)
              console.error(e)
              setState("idle")
            }
          }}
        >
          {paymentMethod === "resto" ? t("place_order") : t("pay_now_asterisk")}
        </Button>
      </Row>
      {route && (
        <>
          <Divider />
          <GoogleStaticImage route={route} />
        </>
      )}
      <Divider />

      <>
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 4, fontSize: 12 }}
        >
          {t("order_terms_1")}
        </Typography.Paragraph>
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 4, fontSize: 12 }}
        >
          {t("order_terms_2")}
        </Typography.Paragraph>
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 4, fontSize: 12 }}
        >
          {t("order_terms_3")}
        </Typography.Paragraph>
      </>
    </div>
  )
}

export default OrderDrawer
